(() => {
  const QUIZ_ID = "find-your-frames";
  const root = typeof window !== "undefined" ? window : globalThis;
  const MULTI_KEYS = ["shape", "color"];
  const EXCLUSIVE = { shape: "surprise", color: "any" };

  const LENS_TO_BUCKET = {
    sunglasses: "sunglasses",
    reading: "reading",
    blue_light: "blue_light",
    unsure: "sunglasses",
  };

  const SHAPE_ALIASES = {
    round: ["round", "oval", "circle"],
    square: ["square", "rectangle", "rectangular", "rect", "wayfarer"],
    "cat-eye": ["cat-eye", "cateye", "cat eye", "cat_eye"],
    aviator: ["aviator", "teardrop", "tear drop", "pilot"],
    oversized: ["oversized", "oversize", "over sized", "over-sized"],
  };

  const COLOR_ALIASES = {
    black: ["black", "jet", "ebony", "noir", "ink"],
    tortoise: ["tortoise", "tortoiseshell", "tort", "havana", "amber"],
    pop: ["red", "blue", "green", "pink", "yellow", "purple", "orange", "crystal", "clear", "translucent", "multicolor", "multi color"],
    neutral: ["gold", "silver", "metal", "champagne", "beige", "cream", "nude", "gunmetal", "pewter", "tan", "khaki", "bronze", "copper", "grey", "gray", "white", "rose gold"],
  };

  const LENS_ALIASES = {
    sunglasses: ["sun", "sunglasses", "polarized", "sunglass"],
    reading: ["reading", "reader", "readers"],
    blue_light: ["blue light", "bluelight", "blue-light", "blue_light"],
  };

  const GENDER_ALIASES = {
    mens: ["mens", "men", "male"],
    womens: ["womens", "women", "female"],
  };

  const UNISEX_ALIASES = ["unisex", "all gender", "allgender"];

  function normalize(value) {
    return String(value || "")
      .toLowerCase()
      .replace(/['’]/g, "")
      .replace(/[_/,;]+/g, " ")
      .replace(/-/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  function haystackHas(haystack, aliases) {
    const hay = ` ${normalize(haystack)} `;
    return (aliases || []).some((alias) => hay.includes(` ${normalize(alias)} `));
  }

  function selectedList(value) {
    if (Array.isArray(value)) return value.filter(Boolean);
    return value ? [value] : [];
  }

  function joinAnswers(value) {
    return selectedList(value).join(",");
  }

  function isMultiKey(key) {
    return MULTI_KEYS.includes(key);
  }

  function exclusiveValue(key) {
    return EXCLUSIVE[key] || null;
  }

  function toggleMulti(current, value, exclusive) {
    const list = selectedList(current);
    if (exclusive && value === exclusive) {
      return list.length === 1 && list[0] === exclusive ? [] : [exclusive];
    }

    const withoutExclusive = exclusive ? list.filter((item) => item !== exclusive) : list;
    if (withoutExclusive.includes(value)) {
      return withoutExclusive.filter((item) => item !== value);
    }
    return withoutExclusive.concat(value);
  }

  function bucketForLens(lens) {
    return LENS_TO_BUCKET[lens] || "sunglasses";
  }

  function matchesAny(haystack, values, aliases) {
    return values.some((value) => aliases[value] && haystackHas(haystack, aliases[value]));
  }

  function genderHaystack(record) {
    return [record.gender, record.tags].join(" ");
  }

  function matchesGenderFilter(record, gender) {
    if (!gender || gender === "either") return true;
    const hay = genderHaystack(record);
    if (!normalize(hay)) return true;
    if (haystackHas(hay, GENDER_ALIASES[gender] || [])) return true;
    if (haystackHas(hay, UNISEX_ALIASES)) return true;
    return false;
  }

  function scoreProduct(record, answers) {
    let hits = 0;
    const shapeHay = [record.shape, record.style, record.title, record.tags].join(" ");
    const colorHay = [record.color, record.style, record.title, record.tags].join(" ");
    const lensHay = [record.lens, record.style, record.tags, record.title].join(" ");
    const genderHay = genderHaystack(record);

    const shapes = selectedList(answers.shape).filter((value) => value !== "surprise");
    const colors = selectedList(answers.color).filter((value) => value !== "any");

    if (shapes.length && matchesAny(shapeHay, shapes, SHAPE_ALIASES)) hits += 1;
    if (colors.length && matchesAny(colorHay, colors, COLOR_ALIASES)) hits += 1;
    if (answers.lens && answers.lens !== "unsure" && haystackHas(lensHay, LENS_ALIASES[answers.lens] || [])) hits += 1;
    if (answers.gender && haystackHas(genderHay, GENDER_ALIASES[answers.gender] || [])) hits += 1;
    if (record.available) hits += 0.01;

    return hits;
  }

  function pickResults(records, answers, limit) {
    const cap = Math.min(8, Math.max(1, Number(limit) || 8));
    const bucket = bucketForLens(answers.lens);
    const seen = new Set();
    const unique = [];

    (records || []).forEach((record) => {
      const handle = record.handle;
      if (!handle || seen.has(handle)) return;
      const buckets = String(record.bucket || "").split(/\s+/).filter(Boolean);
      if (!buckets.includes(bucket)) return;
      if (!matchesGenderFilter(record, answers.gender)) return;
      seen.add(handle);
      unique.push(record);
    });

    const surprise = selectedList(answers.shape).includes("surprise");
    const scored = unique.map((record) => {
      let score = scoreProduct(record, answers);
      if (surprise) score += Math.random() * 0.2;
      return { record, score };
    });

    scored.sort((a, b) => b.score - a.score);
    return scored.slice(0, cap).map((row) => row.record);
  }

  function splitResults(matches) {
    const list = matches || [];
    return {
      primary: list[0] || null,
      alternates: list.slice(1, 3),
      more: list.slice(3),
    };
  }

  function shortLabel(label) {
    return String(label || "").split("/")[0].trim();
  }

  function labelFor(labels, group, value) {
    const set = (labels && labels[group]) || {};
    return set[value] || "";
  }

  function joinAnd(parts) {
    const clean = (parts || []).filter(Boolean);
    if (clean.length <= 1) return clean[0] || "";
    if (clean.length === 2) return `${clean[0]} and ${clean[1]}`;
    return `${clean.slice(0, -1).join(", ")}, and ${clean[clean.length - 1]}`;
  }

  function profileChips(answers, labels) {
    const parts = [];
    const gender = shortLabel(labelFor(labels, "gender", answers.gender));
    if (gender) parts.push(gender);

    const lens = shortLabel(labelFor(labels, "lens", answers.lens));
    if (lens) parts.push(lens);

    selectedList(answers.shape).forEach((shape) => {
      if (shape === "surprise") return;
      const label = shortLabel(labelFor(labels, "shape", shape));
      if (label) parts.push(label);
    });

    selectedList(answers.color).forEach((color) => {
      if (color === "any") return;
      const label = shortLabel(labelFor(labels, "color", color));
      if (label) parts.push(label);
    });

    return parts;
  }

  function whyLine(answers, labels) {
    const lens = shortLabel(labelFor(labels, "lens", answers.lens) || "frames").toLowerCase();
    const gender = shortLabel(labelFor(labels, "gender", answers.gender)).toLowerCase();
    const shapes = selectedList(answers.shape)
      .filter((value) => value !== "surprise")
      .map((value) => shortLabel(labelFor(labels, "shape", value)).toLowerCase())
      .filter(Boolean);
    const colors = selectedList(answers.color)
      .filter((value) => value !== "any")
      .map((value) => shortLabel(labelFor(labels, "color", value)).toLowerCase())
      .filter(Boolean);

    const lead = gender ? `${gender} ` : "";
    let core;
    if (shapes.length && colors.length) core = `A ${lead}${joinAnd(shapes)} pair in ${joinAnd(colors)}`;
    else if (shapes.length) core = `A ${lead}${joinAnd(shapes)} pair`;
    else if (colors.length) core = `A ${lead}pair in ${joinAnd(colors)}`;
    else if (gender) core = `A ${gender} pair from our ${lens}`;
    else core = `A pair from our ${lens}`;

    return `${core} that matches what you picked.`;
  }

  function quizTags(answers) {
    const tags = ["quiz-completed"];
    if (answers.lens) tags.push(`quiz-lens-${answers.lens}`);
    if (answers.gender) tags.push(`quiz-gender-${answers.gender}`);
    selectedList(answers.shape).forEach((shape) => tags.push(`quiz-shape-${shape}`));
    selectedList(answers.color).forEach((color) => tags.push(`quiz-color-${color}`));
    return tags;
  }

  function customProperties(answers, resultCount) {
    return {
      quiz_id: QUIZ_ID,
      quiz_lens: answers.lens || "",
      quiz_gender: answers.gender || "",
      quiz_shape: joinAnswers(answers.shape),
      quiz_color: joinAnswers(answers.color),
      quiz_reader_strength_status: answers.reader_strength || "",
      quiz_completed_at: new Date().toISOString(),
      quiz_result_count: Number(resultCount) || 0,
    };
  }

  function stepCountWord(count) {
    const words = { 1: "One", 2: "Two", 3: "Three", 4: "Four", 5: "Five", 6: "Six" };
    return words[count] || String(count);
  }

  function ledeForCount(template, count) {
    const source = template || "Four short questions. We will show you pairs that fit.";
    const word = stepCountWord(count);
    const withNumber = source.replace(/\b(One|Two|Three|Four|Five|Six|[1-6])\b/, word);
    if (count === 1) return withNumber.replace(/\bquestions\b/, "question");
    return withNumber.replace(/\bquestion\b/, "questions");
  }

  function colorAliasesForAnswers(answers) {
    return selectedList(answers.color)
      .filter((value) => value !== "any")
      .flatMap((value) => COLOR_ALIASES[value] || []);
  }

  function swatchMatchesColor(swatch, aliases) {
    if (!aliases.length) return false;
    const label = [
      swatch.getAttribute("data-tooltip"),
      swatch.getAttribute("aria-label"),
      swatch.textContent,
    ].join(" ");
    return haystackHas(label, aliases);
  }

  const JaxyQuizLogic = {
    QUIZ_ID,
    LENS_TO_BUCKET,
    SHAPE_ALIASES,
    COLOR_ALIASES,
    LENS_ALIASES,
    GENDER_ALIASES,
    UNISEX_ALIASES,
    MULTI_KEYS,
    EXCLUSIVE,
    normalize,
    haystackHas,
    selectedList,
    joinAnswers,
    isMultiKey,
    exclusiveValue,
    toggleMulti,
    bucketForLens,
    matchesAny,
    matchesGenderFilter,
    scoreProduct,
    pickResults,
    splitResults,
    shortLabel,
    profileChips,
    whyLine,
    quizTags,
    customProperties,
    stepCountWord,
    ledeForCount,
    colorAliasesForAnswers,
    swatchMatchesColor,
  };

  root.JaxyQuizLogic = JaxyQuizLogic;

  function readConfig(host) {
    const node = host.querySelector("[data-jaxy-quiz-config]");
    if (!node) return {};
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return {};
    }
  }

  function recordFromCandidate(el) {
    return {
      el,
      handle: el.getAttribute("data-handle") || "",
      bucket: el.getAttribute("data-bucket") || "",
      shape: el.getAttribute("data-shape") || "",
      color: el.getAttribute("data-color") || "",
      style: el.getAttribute("data-style") || "",
      lens: el.getAttribute("data-lens") || "",
      gender: el.getAttribute("data-gender") || "",
      tags: el.getAttribute("data-tags") || "",
      title: el.getAttribute("data-title") || "",
      available: el.getAttribute("data-available") === "true",
    };
  }

  function pushDataLayer(enabled, payload) {
    if (!enabled || typeof root === "undefined") return;
    root.dataLayer = root.dataLayer || [];
    root.dataLayer.push(payload);
  }

  function pushOmnisend(enabled, eventName, details) {
    if (!enabled || typeof root === "undefined" || !root.omnisend || typeof root.omnisend.push !== "function") return;
    const body = Object.assign({ origin: "api" }, details || {});
    root.omnisend.push(["track", eventName, body]);
  }

  function identifyOmnisend(enabled, payload) {
    if (!enabled || typeof root === "undefined") return;
    const api = root.omnisend;
    if (api && typeof api.identifyContact === "function") {
      api.identifyContact(payload);
    }
  }

  if (!root.HTMLElement || !root.customElements || root.customElements.get("jaxy-product-quiz")) {
    return;
  }

  class JaxyProductQuiz extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.config = readConfig(this);
      this.answers = {};
      this.resultHandles = [];
      this.started = false;
      this.completed = false;

      this.progress = this.querySelector("[data-quiz-progress]");
      this.progressBar = this.querySelector("[data-quiz-progress-bar]");
      this.progressLabel = this.querySelector("[data-quiz-progress-label]");
      this.backButton = this.querySelector("[data-quiz-back], .jaxy-quiz__back");
      this.continueButton = this.querySelector("[data-quiz-continue], .jaxy-quiz__continue");
      this.nav = this.querySelector("[data-quiz-nav]");
      this.lede = this.querySelector("[data-quiz-lede]");
      this.panels = Array.from(this.querySelectorAll("[data-quiz-panel]"));
      this.results = this.querySelector("[data-quiz-results]");
      this.resultsTitle = this.querySelector("[data-quiz-results-title]");
      this.resultsSubtitle = this.querySelector("[data-quiz-results-subtitle]");
      this.empty = this.querySelector("[data-quiz-empty]");
      this.emptyTitle = this.querySelector("[data-quiz-empty-title]");
      this.emptyBody = this.querySelector("[data-quiz-empty-body]");
      this.grid = this.querySelector("[data-quiz-grid]");
      this.pool = this.querySelector("[data-quiz-pool]");
      this.picks = this.querySelector("[data-quiz-picks]");
      this.chip = this.querySelector("[data-quiz-chip]");
      this.why = this.querySelector("[data-quiz-why]");
      this.primarySlot = this.querySelector("[data-quiz-primary-slot]");
      this.alternates = this.querySelector("[data-quiz-alternates]");
      this.alternatesGrid = this.querySelector("[data-quiz-alternates-grid]");
      this.more = this.querySelector("[data-quiz-more]");
      this.moreGrid = this.querySelector("[data-quiz-more-grid]");
      this.collectionCta = this.querySelector(".jaxy-quiz__collection-cta");
      this.emailForm = this.querySelector("[data-quiz-email-form]");
      this.emailSuccess = this.querySelector("[data-quiz-email-success]");
      this.emailError = this.querySelector("[data-quiz-email-error]");
      this.live = this.querySelector("[data-quiz-live]");
      this.heading = this.querySelector("[data-quiz-heading]");

      this.candidates = Array.from(this.querySelectorAll("[data-quiz-candidate]")).map(recordFromCandidate);

      this.querySelectorAll("[data-quiz-option]").forEach((button) => {
        button.addEventListener("click", () => this.selectOption(button));
        button.addEventListener("keydown", (event) => this.onOptionKeydown(event, button));
      });

      if (this.backButton) {
        this.backButton.addEventListener("click", () => this.goBack());
      }

      if (this.continueButton) {
        this.continueButton.addEventListener("click", () => {
          if (this.continueButton.disabled || this.continueButton.getAttribute("aria-disabled") === "true") return;
          this.goNext();
        });
      }

      const restart = this.querySelector(".jaxy-quiz__restart");
      if (restart) restart.addEventListener("click", () => this.restart());

      const resultsBack = this.querySelector(".jaxy-quiz__results-back");
      if (resultsBack) resultsBack.addEventListener("click", () => this.goBack());

      if (this.emailForm) {
        this.emailForm.addEventListener("submit", (event) => {
          event.preventDefault();
          this.submitEmail();
        });
      }

      this.showPanel(this.visibleKeys()[0]);
      this.trackStarted();
    }

    visibleKeys() {
      const keys = ["lens", "gender", "shape", "color"];
      if (this.config.show_reader_step !== false && this.answers.lens === "reading") {
        keys.push("reader_strength");
      }
      return keys;
    }

    currentKey() {
      const active = this.panels.find((panel) => !panel.hasAttribute("hidden") && panel.getAttribute("data-quiz-panel") !== "results");
      return active ? active.getAttribute("data-quiz-panel") : null;
    }

    answersFor(key) {
      return selectedList(this.answers[key]);
    }

    hasAnswer(key) {
      return this.answersFor(key).length > 0;
    }

    showPanel(key) {
      this.panels.forEach((panel) => {
        const match = panel.getAttribute("data-quiz-panel") === key;
        panel.toggleAttribute("hidden", !match);
      });

      if (this.results) this.results.hidden = key === "results" ? false : true;

      const keys = this.visibleKeys();
      const index = Math.max(0, keys.indexOf(key));
      const total = keys.length;
      const onResults = key === "results";
      const valueNow = onResults ? total : index + 1;
      const valueMax = total;

      this.classList.toggle("is-compact", key !== "lens");
      this.updateLede(total);

      if (this.progress) {
        this.progress.setAttribute("aria-valuenow", String(valueNow));
        this.progress.setAttribute("aria-valuemax", String(valueMax));
        this.progress.setAttribute("aria-valuemin", "1");
      }

      if (this.progressBar) {
        const percent = onResults ? 100 : Math.round(((index + 1) / valueMax) * 100);
        this.progressBar.style.width = `${percent}%`;
      }

      if (this.progressLabel) {
        this.progressLabel.textContent = onResults
          ? this.config.copy && this.config.copy.progress_done
            ? this.config.copy.progress_done
            : "Matches"
          : `${valueNow} of ${valueMax}`;
      }

      this.syncNav(key, index, onResults);
      this.restoreOptionState(key);

      const focusTarget = key === "results"
        ? (this.resultsTitle || this.results)
        : this.querySelector(`[data-quiz-panel="${key}"] [data-quiz-question]`);

      if (focusTarget && typeof focusTarget.focus === "function") {
        focusTarget.setAttribute("tabindex", "-1");
        focusTarget.focus({ preventScroll: true });
      }

      this.announce(onResults ? (this.resultsTitle ? this.resultsTitle.textContent : "Your matches") : `Question ${valueNow} of ${valueMax}`);
    }

    updateLede(total) {
      if (!this.lede) return;
      const template = (this.config.copy && this.config.copy.subheading) || this.lede.getAttribute("data-lede-template") || this.lede.textContent;
      if (!this.lede.getAttribute("data-lede-template")) {
        this.lede.setAttribute("data-lede-template", template);
      }
      this.lede.textContent = JaxyQuizLogic.ledeForCount(this.lede.getAttribute("data-lede-template"), total);
    }

    syncNav(key, index, onResults) {
      const showBack = onResults || index > 0;
      if (this.backButton) {
        this.backButton.hidden = false;
        this.backButton.disabled = !showBack;
        this.backButton.setAttribute("aria-disabled", showBack ? "false" : "true");
      }

      if (this.continueButton) {
        this.continueButton.hidden = onResults;
        this.updateContinue(key, onResults);
      }

      if (this.nav) this.nav.hidden = onResults;
    }

    updateContinue(key, onResults) {
      if (!this.continueButton) return;
      const current = key || this.currentKey();
      const ready = !onResults && current && this.hasAnswer(current);
      this.continueButton.disabled = !ready;
      this.continueButton.setAttribute("aria-disabled", ready ? "false" : "true");
    }

    announce(message) {
      if (!this.live) return;
      this.live.textContent = "";
      this.live.textContent = message;
    }

    optionsInPanel(panel) {
      return Array.from(panel.querySelectorAll("[data-quiz-option]"));
    }

    panelFor(key) {
      return this.querySelector(`[data-quiz-panel="${key}"]`);
    }

    restoreOptionState(key) {
      const panel = this.panelFor(key);
      if (!panel) return;
      this.paintOptions(panel, this.answersFor(key));
    }

    paintOptions(panel, selected) {
      const chosen = selectedList(selected);
      this.optionsInPanel(panel).forEach((option) => {
        const value = option.getAttribute("data-value");
        const on = chosen.includes(value);
        option.setAttribute("aria-checked", on ? "true" : "false");
        option.classList.toggle("is-selected", on);
      });
    }

    onOptionKeydown(event, button) {
      const panel = button.closest("[data-quiz-panel]");
      if (!panel) return;
      const options = this.optionsInPanel(panel);
      const index = options.indexOf(button);
      let next = index;

      if (event.key === "ArrowDown" || event.key === "ArrowRight") {
        next = (index + 1) % options.length;
      } else if (event.key === "ArrowUp" || event.key === "ArrowLeft") {
        next = (index - 1 + options.length) % options.length;
      } else if (event.key === "Home") {
        next = 0;
      } else if (event.key === "End") {
        next = options.length - 1;
      } else {
        return;
      }

      event.preventDefault();
      options[next].focus();
    }

    selectOption(button) {
      const panel = button.closest("[data-quiz-panel]");
      if (!panel) return;
      const key = panel.getAttribute("data-quiz-panel");
      const value = button.getAttribute("data-value");
      if (!key || !value) return;

      if (isMultiKey(key)) {
        this.answers[key] = toggleMulti(this.answers[key], value, exclusiveValue(key));
        this.paintOptions(panel, this.answers[key]);
        this.updateContinue(key, false);
        this.trackStep(key, joinAnswers(this.answers[key]));
        return;
      }

      this.answers[key] = value;
      this.paintOptions(panel, [value]);
      this.updateContinue(key, false);
      this.trackStep(key, value);
      this.goNext();
    }

    goNext() {
      const key = this.currentKey();
      if (!key || !this.hasAnswer(key)) return;

      const keys = this.visibleKeys();
      const index = keys.indexOf(key);
      if (index === -1) return;

      if (index >= keys.length - 1) {
        this.complete();
        return;
      }

      this.showPanel(keys[index + 1]);
    }

    goBack() {
      if (this.backButton && this.backButton.disabled) return;
      const key = this.currentKey();
      if (key === "results" || !key) {
        const keys = this.visibleKeys();
        this.showPanel(keys[keys.length - 1]);
        return;
      }

      const keys = this.visibleKeys();
      const index = keys.indexOf(key);
      if (index > 0) this.showPanel(keys[index - 1]);
    }

    restart() {
      this.answers = {};
      this.resultHandles = [];
      this.completed = false;
      this.querySelectorAll("[data-quiz-option]").forEach((option) => {
        option.setAttribute("aria-checked", "false");
        option.classList.remove("is-selected");
      });
      this.returnCardsToPool();
      this.clearSwatchHighlights();
      if (this.chip) {
        this.chip.hidden = true;
        this.chip.replaceChildren();
      }
      if (this.why) {
        this.why.hidden = true;
        this.why.textContent = "";
      }
      if (this.picks) this.picks.hidden = true;
      if (this.alternates) this.alternates.hidden = true;
      if (this.more) this.more.hidden = true;
      if (this.emailForm) {
        this.emailForm.hidden = false;
        this.emailForm.reset();
      }
      if (this.emailSuccess) this.emailSuccess.hidden = true;
      if (this.emailError) this.emailError.hidden = true;
      this.showPanel("lens");
    }

    returnCardsToPool() {
      this.candidates.forEach((record) => {
        record.el.classList.remove("is-visible", "is-primary", "is-alternate", "is-more");
        if (this.pool) this.pool.appendChild(record.el);
      });
    }

    renderProfile() {
      const chips = JaxyQuizLogic.profileChips(this.answers, this.config.labels);
      if (!this.chip) return;
      this.chip.replaceChildren();
      if (!chips.length) {
        this.chip.hidden = true;
        return;
      }
      chips.forEach((text) => {
        const item = document.createElement("span");
        item.className = "jaxy-quiz__chip-item";
        item.textContent = text;
        this.chip.appendChild(item);
      });
      this.chip.hidden = false;
    }

    highlightMatchingSwatches() {
      const aliases = JaxyQuizLogic.colorAliasesForAnswers(this.answers);
      this.querySelectorAll(".color-swatch").forEach((swatch) => {
        const match = JaxyQuizLogic.swatchMatchesColor(swatch, aliases);
        swatch.classList.toggle("is-quiz-match", match);
      });
    }

    clearSwatchHighlights() {
      this.querySelectorAll(".color-swatch.is-quiz-match").forEach((swatch) => {
        swatch.classList.remove("is-quiz-match");
      });
    }

    placeMatches(matches) {
      this.returnCardsToPool();
      const split = JaxyQuizLogic.splitResults(matches);

      if (this.why) {
        this.why.textContent = split.primary ? JaxyQuizLogic.whyLine(this.answers, this.config.labels) : "";
        this.why.hidden = !split.primary;
      }

      if (split.primary && this.primarySlot) {
        split.primary.el.classList.add("is-visible", "is-primary");
        this.primarySlot.appendChild(split.primary.el);
      }

      if (this.alternates) this.alternates.hidden = split.alternates.length === 0;
      split.alternates.forEach((record) => {
        record.el.classList.add("is-visible", "is-alternate");
        if (this.alternatesGrid) this.alternatesGrid.appendChild(record.el);
      });

      if (this.more) this.more.hidden = split.more.length === 0;
      split.more.forEach((record) => {
        record.el.classList.add("is-visible", "is-more");
        if (this.moreGrid) this.moreGrid.appendChild(record.el);
      });

      if (this.picks) this.picks.hidden = matches.length === 0;
      this.highlightMatchingSwatches();
    }

    complete() {
      const limit = this.config.result_limit || 8;
      const matches = JaxyQuizLogic.pickResults(this.candidates, this.answers, limit);
      this.resultHandles = matches.map((record) => record.handle);

      const copy = this.config.copy || {};
      if (this.resultsTitle) this.resultsTitle.textContent = copy.results_title || "Your match";
      if (this.resultsSubtitle) this.resultsSubtitle.textContent = copy.results_subtitle || "";

      this.renderProfile();
      this.placeMatches(matches);

      const empty = matches.length === 0;
      if (this.empty) this.empty.hidden = !empty;
      if (this.grid) this.grid.hidden = true;

      if (empty) {
        const bucket = JaxyQuizLogic.bucketForLens(this.answers.lens);
        if (bucket === "reading") {
          if (this.emptyTitle) this.emptyTitle.textContent = copy.empty_title_reading || copy.empty_title || "No reading glasses yet";
          if (this.emptyBody) this.emptyBody.textContent = copy.empty_body_reading || copy.empty_body || "";
        } else if (bucket === "blue_light") {
          if (this.emptyTitle) this.emptyTitle.textContent = copy.empty_title_blue_light || copy.empty_title || "No blue light frames yet";
          if (this.emptyBody) this.emptyBody.textContent = copy.empty_body_blue_light || copy.empty_body || "";
        } else {
          if (this.emptyTitle) this.emptyTitle.textContent = copy.empty_title || "Nothing to show yet";
          if (this.emptyBody) this.emptyBody.textContent = copy.empty_body || "";
        }
      }

      this.updateCollectionCta();
      this.showPanel("results");
      this.trackCompleted();
    }

    updateCollectionCta() {
      if (!this.collectionCta) return;
      const collections = this.config.collections || {};
      const bucket = JaxyQuizLogic.bucketForLens(this.answers.lens);
      const entry = collections[bucket] || collections.sunglasses || {};
      if (entry.url) {
        this.collectionCta.href = entry.url;
        this.collectionCta.hidden = false;
      } else {
        this.collectionCta.hidden = true;
      }
    }

    submitEmail() {
      if (!this.emailForm) return;
      const emailInput = this.emailForm.querySelector('input[type="email"]');
      const consentInput = this.emailForm.querySelector('input[name="marketing_consent"]');
      const email = emailInput ? String(emailInput.value || "").trim() : "";
      const consented = Boolean(consentInput && consentInput.checked);

      if (this.emailError) this.emailError.hidden = true;

      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        if (this.emailError) {
          this.emailError.hidden = false;
          this.emailError.textContent = (this.config.copy && this.config.copy.email_invalid) || "Enter a valid email.";
        }
        if (emailInput) emailInput.focus();
        return;
      }

      const submitButton = this.emailForm.querySelector('[type="submit"]');
      if (submitButton) submitButton.setAttribute("aria-busy", "true");

      const properties = JaxyQuizLogic.customProperties(this.answers, this.resultHandles.length);
      const tags = JaxyQuizLogic.quizTags(this.answers);
      const enableOmnisend = this.config.track_omnisend !== false;

      identifyOmnisend(enableOmnisend, { email });

      const contact = {
        email,
        tags,
        customProperties: properties,
      };

      if (consented) {
        contact.identifiers = [
          {
            type: "email",
            id: email,
            channels: {
              email: { status: "subscribed" },
            },
          },
        ];
      }

      pushOmnisend(enableOmnisend, "quiz_email_captured", {
        contact,
        properties: Object.assign({}, properties, {
          marketing_consent: consented,
          result_handles: this.resultHandles,
        }),
      });

      pushDataLayer(this.config.track_datalayer !== false, {
        event: "quiz_email_captured",
        quiz_id: QUIZ_ID,
        marketing_consent: consented,
        quiz_result_count: this.resultHandles.length,
      });

      if (submitButton) submitButton.removeAttribute("aria-busy");
      this.emailForm.hidden = true;
      if (this.emailSuccess) this.emailSuccess.hidden = false;
      this.announce((this.config.copy && this.config.copy.email_success) || "Saved.");
    }

    trackStarted() {
      if (this.started) return;
      this.started = true;
      pushOmnisend(this.config.track_omnisend !== false, "quiz_started", {
        properties: { quiz_id: QUIZ_ID },
      });
      pushDataLayer(this.config.track_datalayer !== false, {
        event: "quiz_started",
        quiz_id: QUIZ_ID,
      });
    }

    trackStep(questionKey, answer) {
      pushOmnisend(this.config.track_omnisend !== false, "quiz_step", {
        properties: {
          quiz_id: QUIZ_ID,
          question_key: questionKey,
          answer,
        },
      });
      pushDataLayer(this.config.track_datalayer !== false, {
        event: "quiz_step",
        quiz_id: QUIZ_ID,
        question_key: questionKey,
        answer,
      });
    }

    trackCompleted() {
      if (this.completed) return;
      this.completed = true;
      const payload = {
        quiz_id: QUIZ_ID,
        answers: {
          lens: this.answers.lens || "",
          gender: this.answers.gender || "",
          shape: joinAnswers(this.answers.shape),
          color: joinAnswers(this.answers.color),
          reader_strength: this.answers.reader_strength || "",
        },
        result_count: this.resultHandles.length,
        result_handles: this.resultHandles.slice(),
      };
      pushOmnisend(this.config.track_omnisend !== false, "quiz_completed", {
        properties: payload,
      });
      pushDataLayer(this.config.track_datalayer !== false, Object.assign({ event: "quiz_completed" }, payload));
    }
  }

  root.customElements.define("jaxy-product-quiz", JaxyProductQuiz);
})();
